import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Stack,
  Card,
  CircularProgress,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Avatar,
  IconButton,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Paper
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStudentById, updateStudent, deleteStudent } from '../slices/studentsSlice';
import { fetchClasses } from '../slices/classesSlice';
import { fetchBuses } from '../slices/busesSlice';
import { useToast } from '../context/ToastContext';
import { motion } from 'framer-motion';
import { PhotoCamera, AttachFile, Delete, Phone } from '@mui/icons-material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StudentHealthEditSection from '../components/health/StudentHealthEditSection';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const genders = ['ذكر', 'أنثى'];

// Phone code options
const phoneCodeOptions = [
  { value: '972', label: '+972', country: 'Israel' },
  { value: '970', label: '+970', country: 'Palestine' }
];

const EditStudentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const dispatch = useDispatch();
  const { selectedStudent, status: studentStatus } = useSelector(state => state.students);
  const { list: classesList, status: classesStatus } = useSelector((state) => state.classes);
  const { list: busesList, status: busesStatus } = useSelector((state) => state.buses);

  const [student, setStudent] = useState(null);
  const [newAttachment, setNewAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const userRole = localStorage.getItem('role');
  const isManager = userRole === 'manager';

  // Enhanced phone validation that works with country codes
  const isValidPhone = (phone, code = '972') => {
    if (!phone) return false;
    
    // Remove any non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (code === '972') {
      // Israeli phone: should be 10 digits total when including 0 (like 0501234567)
      // Or 9 digits without the leading 0 (like 501234567)
      return /^0\d{9}$/.test(phone) || /^\d{9}$/.test(cleanPhone);
    } else if (code === '970') {
      // Palestinian phone: similar pattern
      return /^0\d{8,9}$/.test(phone) || /^\d{8,9}$/.test(cleanPhone);
    }
    
    return /^0\d{8,10}$/.test(phone); // General fallback
  };

  // Function to format phone number for display
  const formatPhoneForDisplay = (phone, code) => {
    if (!phone) return '';
    
    // If phone already starts with 0, return as is
    if (phone.startsWith('0')) {
      return phone;
    }
    
    // If phone doesn't start with 0, add it
    return `0${phone}`;
  };

  // Function to get full international phone number
  const getFullPhoneNumber = (phone, code) => {
    if (!phone || !code) return '';
    
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Remove leading 0 if present, then add country code
    const phoneWithoutLeadingZero = cleanPhone.startsWith('0') ? cleanPhone.substring(1) : cleanPhone;
    
    return `+${code}${phoneWithoutLeadingZero}`;
  };

  // Fetch initial data
  useEffect(() => {
    dispatch(fetchStudentById(id));
    dispatch(fetchClasses());
    dispatch(fetchBuses());
  }, [id, dispatch]);

  // Initialize form state once all data is loaded
  useEffect(() => {
    if (
      studentStatus === 'succeeded' &&
      classesStatus === 'succeeded' &&
      busesStatus === 'succeeded' &&
      selectedStudent
    ) {
      // Convert IDs to strings during initialization for consistent comparison
      setStudent({
        first_name: selectedStudent.first_name || '',
        second_name: selectedStudent.second_name || '',
        gender: selectedStudent.gender || '',
        birthdate: selectedStudent.birthdate ? selectedStudent.birthdate.split('T')[0] : '',
        student_id: selectedStudent.student_id || '',
        joins_bus: typeof selectedStudent.is_bus_joined === 'boolean' ? selectedStudent.is_bus_joined : false,
        parent_name: selectedStudent.parent_name || '',
        parent_phone: selectedStudent.parent_phone || '',
        parent_phone_code: selectedStudent.parent_phone_code || '972', // Default to Israel
        parent_phone_2: selectedStudent.parent_phone_2 || '',
        parent_phone_2_code: selectedStudent.parent_phone_2_code || '972', // Default to Israel
        address: selectedStudent.address || 'فلسطين',
        parent_email: selectedStudent.parent_email || '',
        school_class: selectedStudent.school_class != null ? String(selectedStudent.school_class) : '',
        bus_id: selectedStudent.bus != null ? String(selectedStudent.bus) : '',
        notes: selectedStudent.note || '',
        date_of_registration: selectedStudent.date_of_registration ? selectedStudent.date_of_registration.split('T')[0] : '',
        current_attachment: selectedStudent.attachment_url || selectedStudent.attachment || null,
      });
      setInitialLoading(false);
    } else if (
        studentStatus === 'loading' ||
        classesStatus === 'loading' ||
        busesStatus === 'loading'
    ) {
        setInitialLoading(true);
    } else if (
        studentStatus === 'failed' ||
        classesStatus === 'failed' ||
        busesStatus === 'failed'
    ) {
        showToast('فشل تحميل البيانات الأولية', 'error');
        setInitialLoading(false);
    }
  }, [selectedStudent, studentStatus, classesList, classesStatus, busesList, busesStatus, showToast]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (files && files[0]) {
      // Handle file upload
      const file = files[0];
      setNewAttachment(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setAttachmentPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreview(null);
      }
    } else {
      setStudent((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : String(value),
        ...(name === 'joins_bus' && !checked && { bus_id: '' })
      }));
    }
  };

  const handleRemoveAttachment = () => {
    setNewAttachment(null);
    setAttachmentPreview(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Enhanced validation logic with phone codes
    const nameRegex = /^[\u0600-\u06FFa-zA-Z\s]+$/;
    const emailRegex = /^\S+@\S+\.\S+$/;

    if (!student.first_name || !nameRegex.test(student.first_name)) {
      showToast('الاسم الأول يجب أن يحتوي على أحرف فقط', 'error');
      return;
    }
    if (!student.second_name || !nameRegex.test(student.second_name)) {
      showToast('اسم العائلة يجب أن يحتوي على أحرف فقط', 'error');
      return;
    }
    if (!student.parent_name || !nameRegex.test(student.parent_name)) {
      showToast('اسم ولي الأمر يجب أن يحتوي على أحرف فقط', 'error');
      return;
    }
    
    // Enhanced phone validation with country codes
    if (!student.parent_phone || !isValidPhone(student.parent_phone, student.parent_phone_code)) {
      const codeLabel = phoneCodeOptions.find(opt => opt.value === student.parent_phone_code)?.label || '+972';
      showToast(`رقم هاتف ولي الأمر غير صحيح للرمز ${codeLabel}. يجب أن يبدأ بـ 0 ويحتوي على الأرقام المناسبة`, 'error');
      return;
    }
    
    // Validate second phone if provided
    if (student.parent_phone_2 && !isValidPhone(student.parent_phone_2, student.parent_phone_2_code)) {
      const codeLabel = phoneCodeOptions.find(opt => opt.value === student.parent_phone_2_code)?.label || '+972';
      showToast(`رقم الهاتف الثاني غير صحيح للرمز ${codeLabel}. يجب أن يبدأ بـ 0 ويحتوي على الأرقام المناسبة`, 'error');
      return;
    }
    
    if (student.parent_email && !emailRegex.test(student.parent_email)) {
        showToast('البريد الإلكتروني لولي الأمر غير صالح', 'error');
        return;
    }
    if (student.student_id && !/^\d+$/.test(student.student_id)) {
      showToast('رقم الهوية يجب أن يحتوي على أرقام فقط', 'error');
      return;
    }
    if (!student.gender || !student.school_class || !student.date_of_registration || !student.birthdate) {
      showToast('يرجى تعبئة جميع الحقول الأساسية المطلوبة (الاسم، الجنس، الصف، تاريخ الميلاد، تاريخ التسجيل)', 'error');
      return;
    }
    if (student.joins_bus && student.bus_id) {
      const selectedBus = busesList.find(bus => String(bus.id) === student.bus_id);
      const isCurrentBus = String(selectedBus?.id) === String(selectedStudent?.bus);
      
      if (selectedBus && !isCurrentBus && selectedBus.student_count >= selectedBus.capacity) {
        showToast('الباص المختار ممتلئ، يرجى اختيار باص آخر', 'error');
        setLoading(false);
        return;
      }
    }

    setLoading(true);

    // Create FormData for file upload support
    const formData = new FormData();
    const addressValue = student.address?.trim() || 'فلسطين';

    // Add all student data including phone codes
    formData.append('first_name', student.first_name);
    formData.append('second_name', student.second_name);
    formData.append('gender', student.gender);
    formData.append('birthdate', student.birthdate);
    formData.append('student_id', student.student_id || '');
    formData.append('address', addressValue);
    formData.append('is_bus_joined', student.joins_bus);
    formData.append('bus', student.joins_bus && student.bus_id ? student.bus_id : '');
    formData.append('parent_name', student.parent_name);
    
    // Add phone numbers and their codes
    formData.append('parent_phone', student.parent_phone);
    formData.append('parent_phone_code', student.parent_phone_code);
    formData.append('parent_phone_2', student.parent_phone_2 || '');
    formData.append('parent_phone_2_code', student.parent_phone_2_code);
    
    formData.append('parent_email', student.parent_email || '');
    formData.append('school_class', student.school_class || '');
    formData.append('note', student.notes || '');
    formData.append('date_of_registration', student.date_of_registration);
    
    // Add new attachment if provided
    if (newAttachment) {
      formData.append('attachment', newAttachment);
    }

    // Debug: Log the phone data being sent
    console.log('Sending phone data:', {
      parent_phone: student.parent_phone,
      parent_phone_code: student.parent_phone_code,
      parent_phone_2: student.parent_phone_2,
      parent_phone_2_code: student.parent_phone_2_code,
      full_phone_1: getFullPhoneNumber(student.parent_phone, student.parent_phone_code),
      full_phone_2: getFullPhoneNumber(student.parent_phone_2, student.parent_phone_2_code)
    });

    dispatch(updateStudent({ id, updatedData: formData }))
      .unwrap()
      .then(() => {
        showToast('تم تحديث بيانات الطالب بنجاح!', 'success');
        navigate('/students');
      })
      .catch((error) => {
          console.error('Update failed:', error);
          let errorMessage = 'فشل التحديث، حاول مجددًا';
          
          if (error?.response?.data) {
            const data = error.response.data;
            const messages = [];
            
            for (const key in data) {
              if (Array.isArray(data[key])) {
                messages.push(...data[key]);
              } else if (typeof data[key] === 'string') {
                messages.push(data[key]);
              }
            }
            
            if (messages.length > 0) {
              errorMessage = messages.join(' / ');
            }
          }
          
          showToast(errorMessage, 'error');
      })
      .finally(() => setLoading(false));
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

   const handleConfirmDelete = () => {
    setOpenDialog(false);
    
    dispatch(deleteStudent(id))
      .unwrap()
      .then((response) => {
        showToast(response.message || 'تم حذف الطالب بنجاح!', 'success');
        navigate('/students');
      })
      .catch((error) => {
       showToast(error.message || 'فشل في حذف الطالب', 'error');
      });
  };

  const isImageFile = (url) => {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // 🎨 Design System shared styles
  const FONT = 'Cairo, Tajawal, sans-serif';
  const fieldSx = {
    fontFamily: FONT,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      fontFamily: FONT,
      bgcolor: '#fff',
      '& fieldset': { borderColor: '#E5E7EB' },
      '&:hover fieldset': { borderColor: '#4338CA' },
      '&.Mui-focused fieldset': { borderColor: '#4338CA' },
    },
    '& .MuiInputLabel-root': { fontFamily: FONT, color: '#6B7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#4338CA' },
    '& .MuiFormHelperText-root': { fontFamily: FONT },
  };
  const sectionCardSx = {
    borderRadius: 3,
    p: { xs: 2, sm: 3 },
    bgcolor: '#fff',
    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
    border: '1px solid #E5E7EB',
  };
  const sectionLabelSx = {
    fontWeight: 700,
    color: '#1E1B4B',
    fontFamily: FONT,
  };
  const cardMotion = {
    component: motion.div,
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.35, delay: 0.1, ease: 'easeOut' },
    elevation: 0,
  };

  if (initialLoading) return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', bgcolor: '#F8F9FF' }}>
          <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
  );

  if (!student) return (
      <Box sx={{ textAlign: 'center', mt: 5, direction: 'rtl', fontFamily: FONT }}>
          <Typography variant="h6" color="error" sx={{ fontFamily: FONT, fontWeight: 700 }}>لم يتم العثور على بيانات الطالب أو حدث خطأ.</Typography>
          <Button variant="outlined" onClick={() => navigate('/students')} sx={{ mt: 2, fontFamily: FONT, textTransform: 'none', borderRadius: 2.5, borderColor: '#4338CA', color: '#4338CA' }}>
              العودة إلى قائمة الطلاب
          </Button>
      </Box>
  );

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonOutlineIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: FONT }}>
                تعديل بيانات الطالب
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}
              >
                تحديث المعلومات الشخصية والأكاديمية ومعلومات ولي الأمر
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/students')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: FONT,
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            العودة إلى القائمة
          </Button>
        </MotionPaper>

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* General Information Card */}
            <MotionCard {...cardMotion} sx={sectionCardSx}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                  <PersonOutlineIcon sx={{ color: '#4338CA' }} />
                  <Typography variant="h6" sx={sectionLabelSx}>المعلومات الأساسية</Typography>
                </Stack>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="الاسم الأول"
                      sx={fieldSx}
                      name="first_name"
                      value={student.first_name} 
                      onChange={handleChange} 
                      required 
                      dir="rtl" 
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="اسم العائلة"
                      sx={fieldSx}
                      name="second_name"
                      value={student.second_name} 
                      onChange={handleChange} 
                      required 
                      dir="rtl" 
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      select 
                      label="الجنس"
                      sx={fieldSx}
                      name="gender"
                      value={student.gender} 
                      onChange={handleChange} 
                      required 
                      dir="rtl"
                    >
                      {genders.map((gender) => (
                        <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="تاريخ الميلاد"
                      sx={fieldSx}
                      name="birthdate"
                      type="date" 
                      value={student.birthdate} 
                      onChange={handleChange} 
                      InputLabelProps={{ shrink: true }} 
                      required 
                      dir="rtl" 
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="العنوان"
                      sx={fieldSx}
                      name="address"
                      value={student.address} 
                      onChange={handleChange} 
                      dir="rtl"
                      placeholder="فلسطين"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="رقم الهوية"
                      sx={fieldSx}
                      name="student_id"
                      value={student.student_id} 
                      onChange={handleChange} 
                      dir="rtl" 
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </MotionCard>

            {/* Academic Information Card */}
            <MotionCard {...cardMotion} sx={sectionCardSx}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                  <SchoolIcon sx={{ color: '#4338CA' }} />
                  <Typography variant="h6" sx={sectionLabelSx}>المعلومات الأكاديمية</Typography>
                </Stack>
                <Grid container spacing={3}>
                   <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="الصف"
                    name="school_class"
                    value={student.school_class}
                    onChange={handleChange}
                    required
                    dir="rtl"
                    sx={{ ...fieldSx, minWidth: '250px', width: '100%' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 250, width: 'auto', minWidth: '250px' } } }}
                  >
                    {classesList.map((cls) => (
                      <MenuItem key={cls.id} value={String(cls.id)}>
                        {cls.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="تاريخ التسجيل"
                    sx={fieldSx}
                    name="date_of_registration"
                    type="date" 
                    value={student.date_of_registration} 
                    onChange={handleChange} 
                    InputLabelProps={{ shrink: true }} 
                    required 
                    dir="rtl" 
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControlLabel
                        control={<Checkbox checked={student.joins_bus} onChange={handleChange} name="joins_bus" />}
                        label="الالتحاق بباص المدرسة"
                        dir="rtl"
                        sx={{ width: '100%', justifyContent: 'flex-start', '& .MuiFormControlLabel-label': { fontFamily: FONT, color: '#1E1B4B', fontWeight: 600 }, '& .MuiCheckbox-root.Mui-checked': { color: '#4338CA' } }}
                    />
                </Grid>
                {student.joins_bus && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="اختر الباص"
                      name="bus_id"
                      value={student.bus_id}
                      onChange={handleChange}
                      required
                      dir="rtl"
                      sx={{ ...fieldSx, minWidth: '250px', width: '100%' }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 250,
                            width: 'auto',
                            minWidth: '250px'
                          }
                        }
                      }}
                    >
                      {busesList.map((bus) => {
                        const isFull = bus.student_count >= bus.capacity;
                        const availableSeats = bus.capacity - bus.student_count;
                        const isCurrentBus = String(bus.id) === student.bus_id;
                        const shouldDisable = isFull && !isCurrentBus;

                        return (
                          <MenuItem
                            key={bus.id}
                            value={String(bus.id)}
                            disabled={shouldDisable}
                            sx={{
                              backgroundColor: shouldDisable ? 'rgba(244, 67, 54, 0.1)' : 'inherit',
                              '&:hover': {
                                backgroundColor: shouldDisable ? 'rgba(244, 67, 54, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(244, 67, 54, 0.7)',
                              }
                            }}
                          >
                            <Box sx={{ width: '100%', direction: 'rtl' }}>
                              <Typography variant="body2" sx={{ fontWeight: shouldDisable ? 'bold' : 'normal' }}>
                                رقم: {bus.bus_number} | السائق: {bus.manager_name || bus.driver_name || 'غير محدد'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: shouldDisable ? 'error.main' : isCurrentBus ? 'primary.main' : 'text.secondary',
                                  display: 'block',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {isCurrentBus && isFull
                                  ? `الباص الحالي (${bus.student_count}/${bus.capacity})`
                                  : shouldDisable
                                    ? `الباص ممتلئ (${bus.student_count}/${bus.capacity})`
                                    : `مقاعد متاحة: ${availableSeats} من ${bus.capacity}`
                                }
                              </Typography>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </TextField>
                    {(() => {
                      const selectedBus = busesList.find(b => String(b.id) === student.bus_id);
                      const driverName = selectedBus?.manager_name || selectedBus?.driver_name;
                      if (!selectedBus || !driverName) return null;
                      return (
                        <Box sx={{ mt: 1, px: 1.5, py: 1, bgcolor: '#E0F2FE', borderRadius: 2, border: '1px solid #BAE6FD', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#0284C7', fontFamily: FONT, fontWeight: 700 }}>
                            السائق:
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#1E1B4B', fontFamily: FONT, fontWeight: 600 }}>
                            {driverName}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </MotionCard>

          {/* Guardian Information Card - Enhanced with Phone Codes */}
          <MotionCard {...cardMotion} sx={sectionCardSx}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                <FamilyRestroomIcon sx={{ color: '#4338CA' }} />
                <Typography variant="h6" sx={sectionLabelSx}>معلومات ولي الأمر</Typography>
              </Stack>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="اسم ولي الأمر"
                    sx={fieldSx}
                    name="parent_name"
                    value={student.parent_name} 
                    onChange={handleChange} 
                    required 
                    dir="rtl" 
                  />
                </Grid>
                
                {/* Primary Phone Number with Country Code */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    
                    <TextField 
                      fullWidth 
                      label="رقم هاتف ولي الأمر"
                      sx={fieldSx}
                      name="parent_phone"
                      value={student.parent_phone} 
                      onChange={handleChange} 
                      required 
                      dir="rtl"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone />
                          </InputAdornment>
                        ),
                      }}
                      helperText={student.parent_phone ? 
                        `الرقم الكامل: ${getFullPhoneNumber(student.parent_phone, student.parent_phone_code)}` 
                        : 'مثال: 0501234567'
                      }
                    />
                    <FormControl sx={{ minWidth: 100, ...fieldSx }}>
                      <InputLabel>الرمز</InputLabel>
                      <Select
                        name="parent_phone_code"
                        value={student.parent_phone_code}
                        onChange={handleChange}
                        label="الرمز"
                        size="medium"
                      >
                        {phoneCodeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                
                {/* Secondary Phone Number with Country Code */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    
                    <TextField 
                      fullWidth 
                      label="رقم هاتف ولي الأمر الثاني (اختياري)"
                      sx={fieldSx}
                      name="parent_phone_2"
                      value={student.parent_phone_2} 
                      onChange={handleChange} 
                      dir="rtl"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone />
                          </InputAdornment>
                        ),
                      }}
                      helperText={student.parent_phone_2 ? 
                        `الرقم الكامل: ${getFullPhoneNumber(student.parent_phone_2, student.parent_phone_2_code)}` 
                        : 'مثال: 0501234567'
                      }
                    />
                    <FormControl sx={{ minWidth: 100, ...fieldSx }}>
                      <InputLabel>الرمز</InputLabel>
                      <Select
                        name="parent_phone_2_code"
                        value={student.parent_phone_2_code}
                        onChange={handleChange}
                        label="الرمز"
                        size="medium"
                      >
                        {phoneCodeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="البريد الإلكتروني لولي الأمر"
                    sx={fieldSx}
                    name="parent_email"
                    type="email" 
                    value={student.parent_email} 
                    onChange={handleChange} 
                    dir="rtl" 
                  />
                </Grid>
              </Grid>
              
              {/* Phone Summary Display */}
              {(student.parent_phone || student.parent_phone_2) && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#E0F2FE',
                  borderRadius: 2,
                  border: '1px solid #BAE6FD'
                }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#0284C7', fontWeight: 700, fontFamily: FONT }}>
                    📱 ملخص أرقام الهاتف:
                  </Typography>
                  {student.parent_phone && (
                    <Typography variant="body2" sx={{ mb: 0.5, fontFamily: FONT, color: '#1E1B4B' }}>
                      الهاتف الأساسي: {getFullPhoneNumber(student.parent_phone, student.parent_phone_code)}
                    </Typography>
                  )}
                  {student.parent_phone_2 && (
                    <Typography variant="body2" sx={{ fontFamily: FONT, color: '#1E1B4B' }}>
                      الهاتف الثانوي: {getFullPhoneNumber(student.parent_phone_2, student.parent_phone_2_code)}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </MotionCard>

          {/* Attachment Upload Card */}
          <MotionCard {...cardMotion} sx={sectionCardSx}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                <AttachFile sx={{ color: '#4338CA' }} />
                <Typography variant="h6" sx={sectionLabelSx}>المرفقات</Typography>
              </Stack>
              
              {/* Current Attachment Display */}
              {student.current_attachment && !newAttachment && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ ...sectionLabelSx, fontWeight: 700 }}>المرفق الحالي:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {isImageFile(student.current_attachment) ? (
                      <Avatar
                        src={student.current_attachment}
                        alt="المرفق الحالي"
                        variant="rounded"
                        sx={{ width: 80, height: 80 }}
                      />
                    ) : (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        backgroundColor: '#EEF2FF',
                        borderRadius: 2
                      }}>
                        <AttachFile sx={{ color: '#4338CA' }} />
                        <Typography variant="body2" sx={{ fontFamily: FONT, color: '#1E1B4B' }}>مرفق موجود</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* New Attachment Upload */}
              <Box sx={{
                border: '1.5px dashed #C7D2FE',
                borderRadius: 2.5,
                p: 3,
                textAlign: 'center',
                backgroundColor: '#F8F9FF',
                mb: 2
              }}>
                <input
                  accept="image/*,application/pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="attachment-upload"
                  type="file"
                  name="attachment"
                  onChange={handleChange}
                />
                <label htmlFor="attachment-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFile />}
                    sx={{
                      mb: 2,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: FONT,
                      borderColor: '#4338CA',
                      color: '#4338CA',
                      '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
                    }}
                  >
                    {student.current_attachment ? 'تغيير المرفق' : 'إضافة مرفق'}
                  </Button>
                </label>
                <Typography variant="body2" sx={{ fontFamily: FONT, color: '#6B7280' }}>
                  يمكن رفع الصور، ملفات PDF، أو مستندات Word
                </Typography>
              </Box>

              {/* New Attachment Preview */}
              {newAttachment && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ ...sectionLabelSx, fontWeight: 700 }}>المرفق الجديد:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {attachmentPreview ? (
                      <Avatar
                        src={attachmentPreview}
                        alt="معاينة المرفق الجديد"
                        variant="rounded"
                        sx={{ width: 80, height: 80 }}
                      />
                    ) : (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        backgroundColor: '#EEF2FF',
                        borderRadius: 2
                      }}>
                        <AttachFile sx={{ color: '#4338CA' }} />
                        <Typography variant="body2" sx={{ fontFamily: FONT, color: '#1E1B4B' }}>{newAttachment.name}</Typography>
                      </Box>
                    )}
                    <IconButton onClick={handleRemoveAttachment} color="error">
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </CardContent>
          </MotionCard>

          {/* HIDDEN TEMPORARILY - الصحة والبيانات الطبية */}
          {/* <StudentHealthEditSection studentId={id} /> */}

          {/* Notes Card */}
           <MotionCard {...cardMotion} sx={sectionCardSx}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                  <NotesIcon sx={{ color: '#4338CA' }} />
                  <Typography variant="h6" sx={sectionLabelSx}>ملاحظات إضافية</Typography>
                </Stack>
                <TextField
                    fullWidth
                    label="ملاحظات"
                    sx={fieldSx}
                    name="notes"
                    value={student.notes}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    dir="rtl"
                />
            </CardContent>
           </MotionCard>

          {/* Action Buttons */}
          <Stack useFlexGap
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 1, pt: 1 }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={loading || initialLoading}
              startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SaveIcon />}
              sx={{
                bgcolor: '#4338CA',
                color: '#fff',
                px: 4,
                py: 1.1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              {loading ? 'جارٍ الحفظ...' : 'حفظ التعديلات'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/students')}
              disabled={loading || initialLoading}
              sx={{
                px: 4,
                py: 1.1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF', color: '#4338CA' },
              }}
            >
              إلغاء
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setOpenDialog(true)}
              disabled={loading || initialLoading}
              startIcon={<DeleteOutlineIcon />}
              sx={{
                px: 4,
                py: 1.1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                borderColor: '#DC2626',
                color: '#DC2626',
                '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
              }}
            >
              حذف الطالب
            </Button>
          </Stack>
        </Stack>
      </form>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCancelDelete}
        dir="rtl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            fontFamily: FONT,
            boxShadow: '0 8px 30px rgba(67,56,202,0.15)',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, color: '#1E1B4B' }}>تأكيد الحذف</DialogTitle>
        <DialogContent sx={{ fontFamily: FONT, color: '#6B7280' }}>هل أنت متأكد أنك تريد حذف هذا الطالب؟ هذا الإجراء لا يمكن التراجع عنه.</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              color: '#6B7280',
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              bgcolor: '#DC2626',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default EditStudentPage;
